import React from "react";
import {
  PlxsText,
  PlxsTextField,
  PlxsCheckbox,
  PlxsTextLink,
} from "@plexlogic/plexus-ui";
import WizardStepHeader from "../WizardStepHeader";
import WizardFooterNav from "../WizardFooterNav";
import { toSentenceCase } from "../../utils/stringUtils";
import { isValidEmail } from "../../utils/validationUtils";
import { scrollToFirstError } from "../../utils/scrollToFirstError";
import { HELP_URLS } from "../../utils/wizardContentRegistry";

interface ScrutineerDetailsProps {
  scrutineerName: string;
  scrutineerEmail: string;
  scrutineerJobTitle: string;
  scrutineerConfirmation: boolean;
  onScrutineerNameChange: (value: string) => void;
  onScrutineerEmailChange: (value: string) => void;
  onScrutineerJobTitleChange: (value: string) => void;
  onScrutineerConfirmationChange: (value: boolean) => void;
  onNext?: () => void;
  onBack?: () => void;
  isLocked?: boolean;
}

const ScrutineerDetails: React.FC<ScrutineerDetailsProps> = ({
  scrutineerName,
  scrutineerEmail,
  scrutineerJobTitle,
  scrutineerConfirmation,
  onScrutineerNameChange,
  onScrutineerEmailChange,
  onScrutineerJobTitleChange,
  onScrutineerConfirmationChange,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const [showErrors, setShowErrors] = React.useState(false);

  const isFormValid = () => {
    return (
      scrutineerName.trim() !== "" &&
      scrutineerEmail.trim() !== "" &&
      scrutineerJobTitle.trim() !== "" &&
      isValidEmail(scrutineerEmail) &&
      scrutineerConfirmation
    );
  };

  const handleNext = () => {
    if (isLocked) {
      onNext?.();
      return;
    }

    setShowErrors(true);

    if (!isFormValid()) {
      scrollToFirstError([
        {
          hasError: scrutineerName.trim() === "",
          testId: "draw-detail-scrutineer-name-input",
        },
        {
          hasError:
            scrutineerEmail.trim() === "" || !isValidEmail(scrutineerEmail),
          testId: "draw-detail-scrutineer-email-input",
        },
        {
          hasError: scrutineerJobTitle.trim() === "",
          testId: "draw-detail-scrutineer-job-title-input",
        },
        {
          hasError: !scrutineerConfirmation,
          testId: "draw-detail-scrutineer-confirmation-checkbox",
        },
      ]);
      return;
    }
    if (onNext) {
      onNext();
    }
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        padding: "0",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          marginBottom: "var(--plexus-space-lg)",
        }}
      >
        <WizardStepHeader
          marginBottom="var(--plexus-space-md)"
          title={toSentenceCase("who's witnessing the draw?")}
          callout={{
            message: "Add the scrutineer present for the draw.",
            tone: "caution",
            dataTestId: "scrutineer-form-callout",
          }}
        />
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          backgroundColor: "var(--plexus-color-surface-50)",
          padding: "var(--plexus-space-lg)",
          borderRadius: "var(--plexus-border-radius-md)",
          border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: "95%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <PlxsTextField
            dataTestId="draw-detail-scrutineer-name-input"
            label="Full name"
            value={scrutineerName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onScrutineerNameChange(e.target.value)
            }
            hasError={showErrors && scrutineerName.trim() === ""}
            errorMessage={
              showErrors && scrutineerName.trim() === ""
                ? "Please enter scrutineer's full name"
                : ""
            }
            placeholder="e.g. Jordan Lee"
            styleOverrides={{ hidePlaceholderEllipsis: true }}
            isDisabled={isLocked}
            readOnly={isLocked}
          />
          <PlxsTextField
            dataTestId="draw-detail-scrutineer-email-input"
            label="Email"
            value={scrutineerEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onScrutineerEmailChange(e.target.value)
            }
            type="email"
            hasError={
              showErrors &&
              (scrutineerEmail.trim() === "" || !isValidEmail(scrutineerEmail))
            }
            errorMessage={
              showErrors && scrutineerEmail.trim() === ""
                ? "Please enter scrutineer's email"
                : showErrors && !isValidEmail(scrutineerEmail)
                ? "Please enter a valid email address"
                : ""
            }
            placeholder="e.g. jordan@example.com"
            styleOverrides={{ hidePlaceholderEllipsis: true }}
            isDisabled={isLocked}
            readOnly={isLocked}
          />
          <PlxsTextField
            dataTestId="draw-detail-scrutineer-job-title-input"
            label="Job title"
            value={scrutineerJobTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onScrutineerJobTitleChange(e.target.value)
            }
            hasError={showErrors && scrutineerJobTitle.trim() === ""}
            errorMessage={
              showErrors && scrutineerJobTitle.trim() === ""
                ? "Please enter scrutineer's job title"
                : ""
            }
            placeholder="e.g. Solicitor"
            styleOverrides={{ hidePlaceholderEllipsis: true }}
            isDisabled={isLocked}
            readOnly={isLocked}
          />
        </div>

        <div
          style={{
            marginTop: "var(--plexus-space-md)",
          }}
        >
          <PlxsCheckbox
            label={
              <PlxsText>
                I confirm this person is present for the draw and meets the
                applicable{" "}
                <PlxsTextLink
                  dataTestId="scrutineer-requirements-help-link"
                  href={HELP_URLS.scrutineerRequirements}
                  target="_blank"
                  iconName="externalLink"
                >
                  scrutineering requirements
                </PlxsTextLink>
                .
              </PlxsText>
            }
            dataTestId="draw-detail-scrutineer-confirmation-checkbox"
            value={scrutineerConfirmation}
            onChange={() =>
              onScrutineerConfirmationChange(!scrutineerConfirmation)
            }
            isDisabled={isLocked}
            hasError={showErrors && !scrutineerConfirmation}
            errorMessage={
              showErrors && !scrutineerConfirmation
                ? "Please confirm that the scrutineer is present"
                : ""
            }
          />
        </div>
      </div>

      {onNext && onBack && (
        <div style={{ width: "100%", maxWidth: "800px" }}>
          <WizardFooterNav
            backButton={{
              dataTestId: "back-scrutineer-details",
              label: toSentenceCase("back"),
              variant: "secondary",
              tone: "neutral",
              onClick: onBack,
            }}
            continueButton={{
              dataTestId: "next-scrutineer-details",
              label: toSentenceCase(isLocked ? "continue" : "next"),
              variant: "primary",
              onClick: handleNext,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ScrutineerDetails;
